import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import { CalendarComponent } from '../components/ui/calendar';
import { motion } from 'framer-motion';
import { Filter, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { statesList, mockAttendanceRecords } from '../data/mockData';

// Reserved for future sorting when needed

const AttendanceRecords: React.FC = () => {
    // For popover calendar
    const [calendarOpen, setCalendarOpen] = useState(false);
    // Filter states
    const [selectedState, setSelectedState] = useState<string>('');
    const [selectedDistrict, setSelectedDistrict] = useState<string>('');
    const [selectedBlock, setSelectedBlock] = useState<string>('');
    const [selectedGP, setSelectedGP] = useState<string>('');
    const [selectedVillage, setSelectedVillage] = useState<string>('');

    type DatePreset = 'today' | 'week' | 'month' | 'fy' | 'custom';
    const [datePreset, setDatePreset] = useState<DatePreset>('month');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    // Place after startDate declaration
    const [calendarDate, setCalendarDate] = useState<Date | undefined>(startDate ? new Date(startDate) : undefined);
    const [loading, setLoading] = useState(false);

    // Pagination state for metrics table
    const [currentPage, setCurrentPage] = useState(1);

    // Cascading dropdown helpers using mockAttendanceRecords
    const getDistricts = (state: string) => {
        const districts = mockAttendanceRecords.filter((r) => r.state === state && r.district).map((r) => r.district!);
        return Array.from(new Set(districts)).filter(Boolean);
    };
    const getBlocks = (state: string, district: string) => {
        const blocks = mockAttendanceRecords.filter((r) => r.state === state && r.district === district && r.block).map((r) => r.block!);
        return Array.from(new Set(blocks)).filter(Boolean);
    };
    const getGPs = (state: string, district: string, block: string) => {
        const gps = mockAttendanceRecords.filter((r) => r.state === state && r.district === district && r.block === block && r.gp).map((r) => r.gp!);
        return Array.from(new Set(gps)).filter(Boolean);
    };
    const getVillages = (state: string, district: string, block: string, gp: string) => {
        const villages = mockAttendanceRecords.filter((r) => r.state === state && r.district === district && r.block === block && r.gp === gp && r.village).map((r) => r.village!);
        return Array.from(new Set(villages)).filter(Boolean);
    };

    // Metrics computation from filtered records
    const filteredRecords = useMemo(() => {
        let filtered = mockAttendanceRecords;
        if (selectedState) filtered = filtered.filter((r) => r.state === selectedState);
        if (selectedDistrict) filtered = filtered.filter((r) => r.district === selectedDistrict);
        if (selectedBlock) filtered = filtered.filter((r) => r.block === selectedBlock);
        if (selectedGP) filtered = filtered.filter((r) => r.gp === selectedGP);
        if (selectedVillage) filtered = filtered.filter((r) => r.village === selectedVillage);
        if (startDate && endDate) filtered = filtered.filter((r) => r.date >= startDate && r.date <= endDate);
        return filtered;
    }, [selectedState, selectedDistrict, selectedBlock, selectedGP, selectedVillage, startDate, endDate]);
    // Simulate loading when filters change
    useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => {
            setLoading(false);
        }, 400); // match mock API delay
        return () => clearTimeout(timer);
    }, [selectedState, selectedDistrict, selectedBlock, selectedGP, selectedVillage, startDate, endDate]);

    const metrics = useMemo(() => {
        const unavailable = null as unknown as number | null;
        // Count unique locations for each granularity
        const numStates = new Set(filteredRecords.map((r) => r.state)).size;
        const numDistricts = new Set(filteredRecords.map((r) => r.district).filter(Boolean)).size;
        const numBlocks = new Set(filteredRecords.map((r) => r.block).filter(Boolean)).size;
        const numGPs = new Set(filteredRecords.map((r) => r.gp).filter(Boolean)).size;
        const numVillages = new Set(filteredRecords.map((r) => r.village).filter(Boolean)).size;
        // Example: total musterrolls and persons with Face Auth
        const totalMusterrollFaceAuth = filteredRecords.filter((r) => r.kpiName.includes('Musterroll')).reduce((acc, r) => acc + (typeof r.kpiValue === 'number' ? r.kpiValue : 0), 0);
        const totalPersonsFaceAuth = filteredRecords.filter((r) => r.kpiName.includes('Face Auth Attendance') || r.kpiName.includes('Active Workers')).reduce((acc, r) => acc + (typeof r.kpiValue === 'number' ? r.kpiValue : 0), 0);
        return [
            { key: 'impl_states', label: 'Number of States Face Auth implemented', value: numStates },
            { key: 'impl_districts', label: 'Number of Districts Face Auth implemented', value: numDistricts },
            { key: 'impl_blocks', label: 'Number of Blocks Face Auth implemented', value: numBlocks },
            { key: 'impl_gps', label: 'Number of GPs Face Auth implemented', value: numGPs },
            { key: 'impl_villages', label: 'Number of Villages Face Auth implemented', value: numVillages },
            { key: 'musterroll_faceauth', label: 'Total number of musterroll where attendance captured using Face Auth', value: totalMusterrollFaceAuth || 0 },
            { key: 'persons_faceauth', label: 'Total persons whose attendance captured using Face Auth', value: totalPersonsFaceAuth || 0 },
            { key: 'attendance_group', label: 'Total Attendance captured using group photo', value: unavailable },
            { key: 'attendance_single', label: 'Total Attendance captured using single photo', value: unavailable },
            { key: 'manual_without_fa', label: 'Total persons whose attendance is captured manually without face auth', value: unavailable },
            { key: 'ekyc_not_done', label: 'Total persons whose eKYC is not done but present in muster roll', value: unavailable },
            { key: 'ekyc_done', label: 'Total persons whose eKYC done and present in muster roll', value: unavailable },
            { key: 'mates_faceauth', label: 'Number of mates capturing Attendance using FaceAuth', value: unavailable },
        ];
    }, [filteredRecords]);

    const [metricsPerPage, setMetricsPerPage] = useState(10);
    const totalMetrics = metrics.length;
    const totalPages = Math.ceil(totalMetrics / metricsPerPage) || 1;
    const paginatedMetrics = metrics.slice((currentPage - 1) * metricsPerPage, currentPage * metricsPerPage);

    // No sorting in metrics view

    // Clear filters
    const clearFilters = () => {
        setSelectedState('');
        setSelectedDistrict('');
        setSelectedBlock('');
        setSelectedGP('');
        setSelectedVillage('');
        setDatePreset('month');
        // dates will recompute via effect
        setCurrentPage(1);
    };

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedState, selectedDistrict, selectedBlock, selectedGP, selectedVillage, startDate, endDate]);

    // Date preset helpers
    const toISODate = (d: Date) => d.toISOString().slice(0, 10);
    const startOfWeek = (d: Date) => {
        const date = new Date(d);
        const day = date.getDay();
        const diff = (day === 0 ? -6 : 1) - day; // Monday start
        date.setDate(date.getDate() + diff);
        date.setHours(0, 0, 0, 0);
        return date;
    };
    const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
    const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const currentFYRange = (d: Date) => {
        const year = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; // FY starts Apr
        const start = new Date(year, 3, 1);
        const end = new Date(year + 1, 2, 31);
        return { start, end };
    };
    const computeAndSetDates = useCallback((preset: DatePreset) => {
        const now = new Date();
        if (preset === 'today') {
            const iso = toISODate(now);
            setStartDate(iso);
            setEndDate(iso);
        } else if (preset === 'week') {
            const s = startOfWeek(now);
            const e = new Date(s);
            e.setDate(s.getDate() + 6);
            setStartDate(toISODate(s));
            setEndDate(toISODate(e));
        } else if (preset === 'month') {
            const s = startOfMonth(now);
            const e = endOfMonth(now);
            setStartDate(toISODate(s));
            setEndDate(toISODate(e));
        } else if (preset === 'fy') {
            const { start, end } = currentFYRange(now);
            setStartDate(toISODate(start));
            setEndDate(toISODate(end));
        }
    }, []);
    useEffect(() => {
        if (datePreset !== 'custom') computeAndSetDates(datePreset);
    }, [datePreset, computeAndSetDates]);

    // Removed old table helpers not used in metrics view

    return (
        <div className="space-y-6">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col space-y-2">
                <h1 className="text-3xl font-bold text-gray-900">Attendance Records</h1>
                <p className="text-gray-600">Detailed view of Face Authentication implementation across different administrative levels</p>
            </motion.div>

            {/* Filters */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <Card>
                    <CardHeader className="p-4 sm:p-6 pb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <CardTitle className="flex items-center space-x-2">
                                    <Filter className="w-5 h-5" />
                                    <span>Filters</span>
                                </CardTitle>
                                <CardDescription>Filter by location and date range</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
                        <form
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4 items-end"
                            onSubmit={(e) => {
                                e.preventDefault(); /* search triggers fetch via filter state change */
                            }}
                        >
                            <div className="space-y-2">
                                <Label>State</Label>
                                <Select
                                    value={selectedState || ''}
                                    onValueChange={(v) => {
                                        setSelectedState(v);
                                        setSelectedDistrict('');
                                        setSelectedBlock('');
                                        setSelectedGP('');
                                        setSelectedVillage('');
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select state" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {statesList.map((state) => (
                                            <SelectItem key={state} value={state}>
                                                {state}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>District</Label>
                                <Select
                                    value={selectedDistrict || ''}
                                    onValueChange={(v) => {
                                        setSelectedDistrict(v);
                                        setSelectedBlock('');
                                        setSelectedGP('');
                                        setSelectedVillage('');
                                    }}
                                    disabled={!selectedState || getDistricts(selectedState).length === 0}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select district" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getDistricts(selectedState).map((district) => (
                                            <SelectItem key={district} value={district}>
                                                {district}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Block</Label>
                                <Select
                                    value={selectedBlock || ''}
                                    onValueChange={(v) => {
                                        setSelectedBlock(v);
                                        setSelectedGP('');
                                        setSelectedVillage('');
                                    }}
                                    disabled={!selectedDistrict || getBlocks(selectedState, selectedDistrict).length === 0}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select block" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getBlocks(selectedState, selectedDistrict).map((blk) => (
                                            <SelectItem key={blk} value={blk!}>
                                                {blk}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Gram Panchayat</Label>
                                <Select
                                    value={selectedGP || ''}
                                    onValueChange={(v) => {
                                        setSelectedGP(v);
                                        setSelectedVillage('');
                                    }}
                                    disabled={!selectedBlock || getGPs(selectedState, selectedDistrict, selectedBlock).length === 0}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select GP" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getGPs(selectedState, selectedDistrict, selectedBlock).map((gp) => (
                                            <SelectItem key={gp} value={gp!}>
                                                {gp}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Village</Label>
                                <Select value={selectedVillage || ''} onValueChange={(v) => setSelectedVillage(v)} disabled={!selectedGP || getVillages(selectedState, selectedDistrict, selectedBlock, selectedGP).length === 0}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select village" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getVillages(selectedState, selectedDistrict, selectedBlock, selectedGP).map((v) => (
                                            <SelectItem key={v} value={v!}>
                                                {v}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" /> Date Range
                                </Label>
                                <Select value={datePreset} onValueChange={(v) => setDatePreset(v as any)}>
                                    <SelectTrigger className="">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="today">Today</SelectItem>
                                        <SelectItem value="week">This Week</SelectItem>
                                        <SelectItem value="month">This Month</SelectItem>
                                        <SelectItem value="fy">This FY</SelectItem>
                                        <SelectItem value="custom">Custom</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex gap-2 justify-end items-end col-span-1 md:col-span-2 lg:col-span-7">
                                <Button type="submit" variant="default" size="sm">
                                    Search
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                                    Clear
                                </Button>
                            </div>
                        </form>
                        {/* Show calendar inputs below when custom date is selected */}
                        {datePreset === 'custom' && (
                            <div className="flex gap-2 items-center mt-4">
                                <input id="calendar-start" type="date" className="border rounded px-3 py-2 min-w-[110px]" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                <span>-</span>
                                <input type="date" className="border rounded px-3 py-2 min-w-[110px]" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                                    <PopoverTrigger asChild>
                                        <button type="button" className="ml-2 p-2 border rounded hover:bg-gray-100" onClick={() => setCalendarOpen((open) => !open)}>
                                            <CalendarIcon className="w-5 h-5 text-gray-500" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="p-0" align="start">
                                        <CalendarComponent
                                            mode="single"
                                            defaultMonth={calendarDate}
                                            numberOfMonths={2}
                                            selected={calendarDate}
                                            onSelect={(date: Date | undefined) => {
                                                setCalendarDate(date);
                                                if (date) {
                                                    setStartDate(date.toISOString().slice(0, 10));
                                                    setCalendarOpen(false);
                                                }
                                            }}
                                            className="rounded-lg border shadow-sm"
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </motion.div>

            {/* Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card>
                    <CardHeader>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <CardTitle className="flex items-center gap-2">Attendance Metrics</CardTitle>
                                <CardDescription>For {datePreset === 'custom' && startDate && endDate ? `${startDate} to ${endDate}` : datePreset === 'today' ? 'Today' : datePreset === 'week' ? 'This Week' : datePreset === 'month' ? 'This Month' : 'This Financial Year'}</CardDescription>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-600">
                                <span>
                                    Showing {Math.min((currentPage - 1) * metricsPerPage + 1, totalMetrics)} to {Math.min(currentPage * metricsPerPage, totalMetrics)} of {totalMetrics} entries
                                </span>
                                <div className="flex items-center gap-2">
                                    <Label>Items per page</Label>
                                    <Select
                                        value={String(metricsPerPage)}
                                        onValueChange={(v) => {
                                            const n = Number(v);
                                            setMetricsPerPage(n);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="w-[110px]">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="5">5</SelectItem>
                                            <SelectItem value="10">10</SelectItem>
                                            <SelectItem value="20">20</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                    <p className="text-gray-600">Loading metrics...</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Metric</TableHead>
                                                <TableHead className="text-right">Value</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {paginatedMetrics.map((m, index) => (
                                                <motion.tr key={m.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="border-b">
                                                    <TableCell className="font-medium">{m.label}</TableCell>
                                                    <TableCell className="text-right font-mono">{m.value == null ? '—' : m.value.toLocaleString()}</TableCell>
                                                </motion.tr>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>

                                {/* Pagination */}
                                <div className="flex items-center justify-between mt-6">
                                    <div className="text-sm text-gray-600">
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
                                            <ChevronLeft className="w-4 h-4" />
                                            Previous
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
                                            Next
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default AttendanceRecords;
